import { useUserStore } from '@/store/user'
import { useExamStore } from '@/store/exam'
import { useExamFilterStore } from '@/store/examFilter'
import { watch } from 'vue'

import ExamAssignPatientOffcanvas from './ExamAssignPatientOffcanvas.vue'
import ExamDownloadOffcanvas from './ExamDownloadOffcanvas.vue'
import ExamExportByMonthOffcanvas from './ExamExportByMonthOffcanvas.vue'
import ExamFilter from './ExamFilter.vue'
import ExamGridList from './ExamGridList.vue'
import ExamNewOffcanvas from './ExamNewOffcanvas.vue'
import ExamTableList from './ExamTableList.vue'

const IS_PRODUCTION = process.env.NODE_ENV == 'production'

export default {
  name: 'exam-list',
  components: {
    ExamAssignPatientOffcanvas,
    ExamDownloadOffcanvas,
    ExamExportByMonthOffcanvas,
    ExamFilter,
    ExamGridList,
    ExamNewOffcanvas,
    ExamTableList
  },
  data: () => ({
    loading: false,
    alert: {
      title: null,
      message: null,
      class: null
    },
    tableExam: {
      items: [],
      total: 0,
      columns: [],
    },
    perPage: 20,
    currentPage: 1,
    totalPages: 0,
    session: null,
    showInputSearch: false,
    showFilterBox: false,
    showValue: null,
    showOffcanvasNewExam: false,
    examImages: [],
    thumbDataPath: null,
    dataPath: null,
    tableType: null,
    showTips: true,
    showMutirao: false,
    showMultiClass: false,
    examFilterStore: useExamFilterStore(),
    isDataReady: false,
    scaleImages: 1,
    isInitialFilterLoad: true,
    hasUrlDataBeenProcessed: false,
    isSmallScreen: false,
    isSpecialist: false,
    responseType: "full",
    hasUnifiedReportFeature: false,
    isPatientNameFetched: false,
  }),
  computed: {
    isCollapsed() {
      let element = document.querySelector('body')
      if (element.classList.contains('retract'))
        return true
    }
  },
  watch: {
    'examFilterStore.filter': {
      handler(newFilterValue, oldFilterValue) {
        const newFilterClone = _.cloneDeep(newFilterValue)
        const oldFilterClone = _.cloneDeep(oldFilterValue)

        if (!oldFilterValue) {
          return;
        }

        // delete newFilterClone.newRequest
        if (oldFilterClone) {
          delete oldFilterClone.newRequest
        }

        const areFiltersDeeplyEqual = _.isEqual(newFilterClone, oldFilterClone);

        if (this.isInitialFilterLoad) {
          if (this.hasAnyFilterCriteria(newFilterValue)) {
            this.performDataFetch(newFilterValue);
          }

          this.isInitialFilterLoad = false;
          return;
        }

        if (!areFiltersDeeplyEqual || newFilterValue.newRequest) {
          this.performDataFetch(newFilterValue);
        } else {
          console.log('The filter has been changed, but has the same values.');
        }
      },
      deep: true,
      // immediate: true
    },
    'tableExam.items': {
      handler(newItems) {
        if (newItems.length > 0) {
          const examStore = useExamStore();
          if (examStore.lastClickedExamId) {
            this.$nextTick(() => {
              this.scrollToLastExam(examStore.lastClickedExamId);
              examStore.setLastClickedExamId(null);
            });
          }
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.$activeLink.value = 'exam'
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.showMutirao = this.session.features.includes('mutirao')
    this.showMultiClass = this.session.features.includes('aiMultiClassEC')
    this.isSpecialist = this.session.isSpecialist
    this.hasUnifiedReportFeature = this.session.features.includes('unifiedReport')

    if (this.session.collaborator.welcomeTips)
      this.showTips = false

    this.applyStoredFilters()
    this.applyStoredTablePreferences()

    this.performDataFetch(this.examFilterStore.filter);

    window.addEventListener('resize', this.handleResize)
    this.handleResize()

  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    hasAnyFilterCriteria(filter) {
      const innerFilter = filter.filter;

      const hasDateFilters = innerFilter.startDate !== null && innerFilter.startDate !== undefined
      const hasEndDate = innerFilter.endDate !== null && innerFilter.endDate !== undefined
      const hasPatientID = innerFilter.patientID !== null && innerFilter.patientID !== undefined

      const hasActiveProperties = innerFilter.properties && Object.keys(innerFilter.properties).some(key =>
        (typeof innerFilter.properties[key] === 'boolean' && innerFilter.properties[key] === true)
      )

      return !!(hasPatientID || hasDateFilters || hasEndDate || hasActiveProperties)
    },

    async fetchDataForFilters(innerFilter) {
      if (innerFilter.patientID && !innerFilter.patientFullName && !this.isPatientNameFetched) {
        try {
          const response = await this.$patientService.get(innerFilter.patientID);
          const patient = response.patient;
          if (patient) {
            this.examFilterStore.refreshFilter({
              filter: { patientFullName: patient.fullName },
              newRequest: false
            });

            this.isPatientNameFetched = true;
          }
        } catch (err) {
          console.error('Failed to fetch patient name:', err);
        }
      }
    },

    performDataFetch(filter) {
      const innerFilter = filter.filter;

      this.fetchDataForFilters(innerFilter);

      this.currentPage = filter.examCurrentPage && !filter.newRequest ? filter.examCurrentPage : 1;

      let params = {
        page: this.currentPage,
        filter: {}
      }

      if (innerFilter.patientID) {
        params.filter.id = innerFilter.patientID;
      }

      if (innerFilter.startDate !== null && innerFilter.startDate !== undefined && innerFilter.startDate !== false) {
        params.filter.startDate = innerFilter.startDate;
      }

      if (innerFilter.endDate !== null && innerFilter.endDate !== undefined && innerFilter.endDate !== false) {
        params.filter.endDate = innerFilter.endDate;
      }

      if (innerFilter.filterSelected) {
        params.filter.statusFilter = innerFilter.filterSelected;
      }

      if (innerFilter.properties) {
        params.filter.properties = { ...innerFilter.properties }
      }

      if (innerFilter.newRequest) {
        this.currentPage = 1
        params.page = 1
        this.$utils.clearUrlFilters(this);
      }

      if (this.$route.query.data && !innerFilter.newRequest && !this.hasUrlDataBeenProcessed) {
        let data = this.$utils.parseDataQuery(this.$route.query.data)

        if (data.startDate) {
          params.filter.startDate = data.startDate;
        }

        if (data.endDate) {
          params.filter.endDate = data.endDate;
        }

        if (data.patientID) {
          params.filter.id = data.patientID;
        }

        if (data.properties) {
          params.filter.properties = { ...(params.properties || {}), ...data.properties };
        }

        if (data.currentPage && data.currentPage > 0) {
          this.currentPage = data.currentPage
          params.page = data.currentPage
          this.hasUrlDataBeenProcessed = true
        }
      }

      this.debounce(params)
    },

    async doFilter(params) {
      const examFilterStore = useExamFilterStore()
      this.alert.message = null

      this.loading = true;
      NProgress.start();

      try {
        let response;

        response = await this.$examService.filteredListLastsWithExamdata(params);

        this.responseType = response.type

        if (response.totalCount === 0) {
          this.alert = this.$message.alertWithTitle(this, 'request_notfound_exam_title', 'request_notfound_gently_text');
          NProgress.done(true);
          this.loading = false;
          this.tableExam = { items: [], total: 0, columns: [] };
          this.totalPages = 1
          return;
        }

        this.loadTableData(response);
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },

    applyStoredTablePreferences() {
      if (localStorage.getItem('examTableType')) {
        this.tableType = localStorage.getItem('examTableType')
      } else {
        this.tableType = 'grid'
      }

      if (localStorage.getItem('examScaleImages')) {
        this.scaleImages = localStorage.getItem('examScaleImages')
      } else {
        this.scaleImages = 1
      }
    },

    applyStoredFilters() {
      const examFilterStore = useExamFilterStore()
      const storedFilters = sessionStorage.getItem('examFilters')
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters)
        examFilterStore.refreshFilter({
          ...parsedFilters,
          // newRequest: false
        })

        this.$nextTick(() => {
          sessionStorage.removeItem('examFilters')
        });
      } else {
        examFilterStore.reset()
        // examFilterStore.refreshFilter({ newRequest: true })
      }
    },
    onPageChange(page) {
      this.currentPage = page

      const examFilterStore = useExamFilterStore();
      const updatedFilterInStore = {
        ...examFilterStore.filter,
        examCurrentPage: page,
        newRequest: false
      };

      examFilterStore.refreshFilter(updatedFilterInStore);
    },

    debounce: _.debounce(function (newParams) {
      this.doFilter(newParams ?? this.examFilterStore.filter)
    }, 250, { 'maxWait': 150 }),

    async fetchData() {
      this.alert.message = null;
      this.loading = true;
      NProgress.start();
      try {
        let response = await this.$examService.listWithExamdata();
        this.loadTableData(response);
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    async fetchDataImage(examID) {
      this.loading = true
      NProgress.start()
      try {

        let response = await this.$examDataService.list({ id: examID });

        this.thumbDataPath = `${response.thumbDataPath}`
        this.dataPath = `${response.dataPath}`

        // TODO move to service
        if (!IS_PRODUCTION) {
          this.thumbDataPath = `http://localhost:1337${response.thumbDataPath}`
          this.dataPath = `http://localhost:1337${response.dataPath}`
        }

      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
      this.loading = false

      return examImages
    },
    loadTableData(response) {
      this.alert = {}
      response.result.forEach(async (item) => {
        try {
          item.hasNoAlteration = false // TODO need backend
          item.status = this.$i18n.t(item.status);

          this.$aiService.insertScoreResultInto(item, this.showMultiClass)
        } catch (error) {
          item.scoreResult = 'error'
        }
      });

      try {
        this.thumbDataPath = `${response.thumbDataPath}`
        this.dataPath = `${response.dataPath}`

        // TODO move to service
        if (!IS_PRODUCTION) {
          this.thumbDataPath = `http://localhost:1337${response.thumbDataPath}`
          this.dataPath = `http://localhost:1337${response.dataPath}`
        }

        // {key:"favorite", name: '', type: 'custom', value: 'actions', width: '2%'},
        let self = this
        this.tableExam = {
          'items': response.result,
          'total': response.totalCount,
          'columns': [
            { key: 'name', name: this.$i18n.t('exam.name'), type: 'string', value: 'patient.fullName', width: '20%' },
            { key: 'captures', name: this.$i18n.t('exam.captures'), type: 'custom', value: 'captures', width: '13%' },
            { key: 'ai', name: this.$i18n.t('exam.statusAI'), type: 'custom', value: 'status', width: '13%' },
            { key: 'content', name: this.$i18n.t(this.hasUnifiedReportFeature ? 'exam.examIs' : 'exam.content'), type: 'custom', value: 'actions', width: '40%' },
            { name: this.$i18n.t('exam.date'), type: 'date', value: 'date', width: '14%' },
          ]
        };

        if (this.responseType == "filtered"){
          this.totalPages = -1;
        } else {
          this.totalPages = Math.ceil(response.totalCount / this.perPage);
        }


        if (response.result.length === 0) {
          this.tableExam = {};
          this.alert = this.$message.alertWithTitle(this, 'request_notfound_exam_title', 'request_notfound_gently_text');
          // this.alert = this.$message.alert(this, 'request_notfound_gently', 'warning')
          // this.$message.popup(this, 'request_notfound', 'operationWarning', 'warn')
        }
      } catch (err) {
        console.log('error load table', err);
      }

    },
    examSelected(values) {
      if (!values) {
        return
      }
      let item = values.item

      if (!item) {
        return
      }

      const examFilterStore = useExamFilterStore()
      examFilterStore.closeFilterExplicitly()

      const examStore = useExamStore();
      examStore.setLastClickedExamId(item.id);

      let data = null

      const currentFilter = this.examFilterStore.filter;
      const currentInnerFilter = currentFilter.filter;

      if (!_.isEmpty(currentFilter)) {
        const filterToSave = {
          newRequest: currentFilter.newRequest,
          examCurrentPage: this.currentPage,

          filter: {
            startDate: currentInnerFilter.startDate,
            endDate: currentInnerFilter.endDate,
            patientID: currentInnerFilter.patientID,
            patientFullName: currentInnerFilter.patientFullName,
            filterSelected: currentInnerFilter.filterSelected,
            properties: { ...currentInnerFilter.properties }
          }
        }

        this.examFilterStore.refreshFilter(filterToSave);
        sessionStorage.setItem('examFilters', JSON.stringify(this.examFilterStore.filter));

        const startDateMoment = moment(currentInnerFilter.startDate, this.$i18n.t('dateFormat'))
        const endDateMoment = moment(currentInnerFilter.endDate, this.$i18n.t('dateFormat'))

        let dataParams = {
          filterSelected: currentFilter.filterSelected,
          currentPage: this.currentPage,
          properties: currentInnerFilter.properties
        };

        if (startDateMoment.isValid()) {
          dataParams.startDate = startDateMoment.format();
        }

        if (endDateMoment.isValid()) {
          dataParams.endDate = endDateMoment.format();
        }

        for (const key in dataParams) {
          if (dataParams[key] === null || dataParams[key] === false || dataParams[key] === undefined) {
            delete dataParams[key];
          }
        }

        if (dataParams.properties) {
          for (const key in dataParams.properties) {
            if (dataParams.properties[key] === false) {
              delete dataParams.properties[key];
            }
          }
          if (_.isEmpty(dataParams.properties)) { // If properties is empty, remove it
            delete dataParams.properties;
          }
        }

        data = this.$utils.dataQuery(dataParams)
      }
      if (values.commandPressed) {
        // Resolve the route
        const routeData = this.$router.resolve({
          name: 'exam-show',
          params: { examID: item.id },
          query: { tab: 'images', data: data }
        });

        // Open the resolved route in a new tab
        window.open(routeData.href, '_blank');
      } else {
        this.$router.push({ name: 'exam-show', params: { examID: item.id }, query: { tab: 'images', data: data } })
      }
    },
    formatDate(date) {
      return moment(date).valueOf();
    },
    checkExamStatus(value) {
      return value == 'closed' ? 'label-closed' : 'label-open'
    },
    doDownload(exam) {
      this.alert.message = null;
      try {
        NProgress.start()
        this.$examService.download(exam.id)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
    },
    doFavorite(item) {
      item.favorite = true
    },
    openOffCanvasNewExam() {
      this.showOffcanvasNewExam = true

      this.$nextTick(() => {
        this.$refs.examNewOffcanvas.show()
      })
    },
    async openOffcanvasExamDownload() {
      const ok = await this.$refs.examDownloadOffcanvas.show({
        title: this.$i18n.t('exam.examsDownload'),
        okButton: 'Download',
        okButtonClass: 'btn-success',
      })
      if (ok) { }
    },
    async openOffcanvasExamExport() {
      const ok = await this.$refs.examExportByMonthOffcanvas.show({
        title: this.$i18n.t('reportExam.title'),
        clinicID: this.session.currentClinic.id,
        okButton: 'Download',
        okButtonClass: 'btn-success',
      })
      if (ok) { }
    },
    async openAssignOffcanvas(item) {
      if (this.isSpecialist)
        return

      const ok = await this.$refs.examAssignPatientOffcanvas.show({
        exam: item
      })
      if (ok) { }
    },
    closeAssignOffCanvas() {
      this.$nextTick(() => {
        this.debounce()
      })
    },
    setTableLocalStorage(value) {
      this.tableType = value
      localStorage.setItem('examTableType', value);
    },
    setScaleImageLocalStorage(scaleImages) {
      localStorage.setItem('examScaleImages', scaleImages);
    },
    closeAndIncrementTipsCount() {
      this.showTips = false
      const userStore = useUserStore()
      userStore.incrementTipsCount(1)
    },
    handleOffcanvasClose(reason) {
      if (reason === 'saved') {
        this.$nextTick(() => {
          this.debounce()
        })
      }
    },
    handleResize() {
      this.isSmallScreen = window.innerWidth < 992;
    },
    scrollToLastExam(examId) {
      const examElement = document.querySelector(`[data-exam-id="${examId}"]`);

      if (examElement) {
        examElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
  },
}
